// Workshop — the Projects-scene landmark. A larger box 0.6x0.4x0.4
// with a glowing emissive mint window and a small antenna on top. A
// stronger point light sits inside to suggest an active workshop.
//
// When `isFar` is true (camera further than ~6 from the landmark),
// the small extras (window glow, antenna, point light) are dropped
// and the body is replaced by a single simplified silhouette so the
// draw call drops to ~2 tris.

import { useMemo } from 'react';
import type { Group } from 'three';

export interface WorkshopProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  isActive?: boolean;
  isHovered?: boolean;
  /** Drop antenna, window, point light, and unify the body into one
   *  silhouette mesh. Used when camera is past the LOD distance. */
  isFar?: boolean;
  groupRef?: React.MutableRefObject<Group | null>;
}

const COLOR_BODY = '#b8a572'; // gold
const COLOR_TRIM = '#f5f0e8'; // warm
const COLOR_WINDOW = '#7fb89f'; // mint
const COLOR_ANTENNA = '#5a5a5a';

export function Workshop({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  isActive = false,
  isHovered = false,
  isFar = false,
  groupRef,
}: WorkshopProps) {
  const combinedScale = useMemo(() => {
    const base = scale;
    const hover = isHovered ? 1.08 : 1;
    const active = isActive ? 1.15 : 1;
    return base * hover * active;
  }, [scale, isHovered, isActive]);

  return (
    <group
      ref={groupRef as React.MutableRefObject<Group>}
      name="landmark-workshop"
      position={position}
      rotation={rotation}
      scale={combinedScale}
    >
      {/* Body. When far we collapse body + roof + door into a single
       *  larger silhouette box so the workshop still reads from a
       *  distance. */}
      {isFar ? (
        <mesh
          name="workshop-silhouette"
          position={[0, 0.28, 0]}
          userData={{ landmarkType: 'workshop' }}
          frustumCulled
        >
          <boxGeometry args={[0.65, 0.5, 0.45]} />
          <meshStandardMaterial color={COLOR_BODY} roughness={0.75} flatShading />
        </mesh>
      ) : (
        <>
          {/* Body — 0.6 x 0.4 x 0.4 box. */}
          <mesh name="workshop-body" position={[0, 0.2, 0]} userData={{ landmarkType: 'workshop' }} frustumCulled>
            <boxGeometry args={[0.6, 0.4, 0.4]} />
            <meshStandardMaterial color={COLOR_BODY} roughness={0.75} flatShading />
          </mesh>

          {/* Roof slab — thin gold box on top. */}
          <mesh name="workshop-roof" position={[0, 0.42, 0]} userData={{ landmarkType: 'workshop' }} frustumCulled>
            <boxGeometry args={[0.65, 0.04, 0.45]} />
            <meshStandardMaterial color={COLOR_TRIM} roughness={0.7} flatShading />
          </mesh>

          {/* Door — warm trim on the front. */}
          <mesh name="workshop-door" position={[-0.18, 0.13, 0.21]} frustumCulled>
            <planeGeometry args={[0.12, 0.22]} />
            <meshStandardMaterial color={COLOR_TRIM} roughness={0.8} flatShading />
          </mesh>
        </>
      )}

      {/* Glowing window — emissive mint plane on the front face.
       *  Only rendered close-up; from a distance it's a single
       *  pixel and the emissive cost isn't worth it. */}
      {!isFar && (
        <mesh name="workshop-window" position={[0.1, 0.22, 0.21]} frustumCulled>
          <planeGeometry args={[0.18, 0.14]} />
          <meshStandardMaterial
            color={COLOR_WINDOW}
            emissive={COLOR_WINDOW}
            emissiveIntensity={isActive ? 0.8 : 0.4}
            roughness={0.4}
            flatShading
          />
        </mesh>
      )}

      {/* Antenna + tip — small extras, suppressed when far. */}
      {!isFar && (
        <>
          <mesh name="workshop-antenna" position={[0.2, 0.55, -0.1]} frustumCulled>
            <cylinderGeometry args={[0.015, 0.015, 0.18, 6]} />
            <meshStandardMaterial color={COLOR_ANTENNA} roughness={0.6} flatShading />
          </mesh>
          <mesh name="workshop-antenna-tip" position={[0.2, 0.66, -0.1]} frustumCulled>
            <sphereGeometry args={[0.025, 8, 6]} />
            <meshStandardMaterial
              color={COLOR_WINDOW}
              emissive={COLOR_WINDOW}
              emissiveIntensity={0.6}
            />
          </mesh>
        </>
      )}

      {/* Stronger point light — workshop is "in use". Dropped far. */}
      {!isFar && (
        <pointLight
          color={COLOR_WINDOW}
          intensity={isActive ? 1.5 : 1.0}
          distance={2.5}
          position={[0.1, 0.25, 0]}
        />
      )}
    </group>
  );
}

export default Workshop;
